import React from 'react';
import { formatPercent } from '@/utils/numberUtils';

interface ModernStatsCardProps {
  icon?: string;
  label: string;
  value: number | string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  bgColor?: string;
  iconBg?: string;
  loading?: boolean;
  subtitle?: string;
}

const ModernStatsCard: React.FC<ModernStatsCardProps> = ({
  icon,
  label,
  value,
  trend,
  bgColor = 'bg-white',
  iconBg = 'bg-[#EEF3F7]',
  loading = false,
  subtitle,
}) => {
  const hasTopRow = Boolean(icon) || Boolean(trend);

  if (loading) {
    return (
      <div
        className={`${bgColor} border border-[#E5E9EB] rounded-3xl p-6 animate-pulse`}
      >
        {hasTopRow ? (
          <div className="flex items-start justify-between mb-4">
            {icon ? (
              <div className="bg-gray-200 w-[56px] h-[56px] rounded-2xl" />
            ) : (
              <span />
            )}
            {trend ? (
              <div className="bg-gray-200 h-6 w-16 rounded-full" />
            ) : null}
          </div>
        ) : null}
        <div className="bg-gray-200 h-3 w-20 rounded mb-3" />
        <div className="bg-gray-200 h-8 w-24 rounded" />
        {subtitle && <div className="bg-gray-200 h-3 w-32 rounded mt-2" />}
      </div>
    );
  }

  return (
    <div
      className={`${bgColor} border border-[#E5E9EB] rounded-3xl p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1`}
    >
      {hasTopRow ? (
        <div className="flex items-start justify-between mb-4">
          {icon ? (
            <div
              className={`${iconBg} w-[56px] h-[56px] rounded-2xl flex items-center justify-center`}
            >
              <img src={icon} alt="" className="w-[28px] h-[28px]" />
            </div>
          ) : (
            <span />
          )}
          {trend && (
            <div
              className={`flex items-center gap-1 px-3 py-1 rounded-full ${
                trend.isPositive
                  ? 'bg-[#E6F8F0] text-[#00AE3E]'
                  : 'bg-[#FFE5E5] text-[#DC2626]'
              }`}
            >
              <span className="text-xs font-semibold">
                {trend.isPositive ? '↑' : '↓'}{' '}
                {formatPercent(Math.abs(trend.value))}
              </span>
            </div>
          )}
        </div>
      ) : null}
      <p className="text-[#9B919D] text-xs font-normal mb-2">{label}</p>
      <p className="text-[#292828] text-3xl font-bold">
        {typeof value === 'number'
          ? value.toLocaleString('en-US', { maximumFractionDigits: 0 })
          : value}
      </p>
      {subtitle && <p className="text-[#9B919D] text-xs mt-2">{subtitle}</p>}
    </div>
  );
};

export default ModernStatsCard;
